import React, { useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import EmptyList from "go-core/components/EmptyList";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi, InvoiceOrderApi } from "../../../../../../../../../../../services/Api/Organization/types";
import InvoicePreviewOrdersModal from "./InvoicePreviewOrdersModal";

interface Props {
	invoice: InvoiceApi;
	setInvoice: (invoice: InvoiceApi) => void;
}

const InvoicePreviewOrdersCard = ({ invoice, setInvoice }: Props) => {
	const { t } = useTranslation();
	const [showInvoiceOrderModal, setShowInvoiceOrderModal] = useState(false);
	const orders = invoice.orders;
	const organization = useSelector(selectOrganization);
	const { addFlash, addSuccessFlash } = useFlash();
	const confirmation = useConfirmation();

	const handleRemoveOrder = async (order: InvoiceOrderApi) => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.remove"),
			});
			const newOrders = invoice.orders.filter(({ order_id }) => order_id !== order.order_id);
			const res = await apiOrganization.updateInvoice(
				invoice.id,
				{
					...invoice,
					orders: newOrders,
				},
				{ include: "all" }
			);
			setInvoice(res);
			addSuccessFlash(t("lib:common.flash.removed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<>
			<Card className="card-box">
				<Card.Body>
					<div className="action-header">
						<h5 className="card-title">{t("modules.invoice.field.orders.title")}</h5>
						<Button variant="add" onClick={() => setShowInvoiceOrderModal(true)}>
							+ {t("lib:common.action.add")}
						</Button>
					</div>
					{orders.length > 0 ? (
						<div className="table-responsive">
							<table className="table table-preview">
								<thead>
									<tr>
										<th>{t("modules.invoice.field.order_number.title")}</th>
										<th>{t("lib:common.word.date")}</th>
										<th className="action" />
									</tr>
								</thead>
								<tbody>
									{orders
										.filter((order) => order?.order_id)
										.map((order) => {
											return (
												<tr key={`order-${order.order_id}`}>
													<td>
														{order.order_id ? (
															<Link
																target="_blank"
																rel="noreferrer"
																to={`/${organization.id}/documents/sales/${order.order_id}`}
															>
																{order.order_number}
															</Link>
														) : (
															order.order_number
														)}
													</td>
													<td>{FormatDate(order.order_sale_date)}</td>
													<td className="action">
														<RemoveSVG
															className="icon"
															onClick={() => handleRemoveOrder(order)}
														/>
													</td>
												</tr>
											);
										})}
								</tbody>
							</table>
						</div>
					) : (
						<EmptyList description={t("modules.invoice.field.empty_orders.title")} />
					)}
				</Card.Body>
			</Card>
			{showInvoiceOrderModal && (
				<InvoicePreviewOrdersModal
					show={showInvoiceOrderModal}
					onHide={() => setShowInvoiceOrderModal(false)}
					invoice={invoice}
					setInvoice={setInvoice}
				/>
			)}
		</>
	);
};

export default InvoicePreviewOrdersCard;
